import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fileUrl } from "@/lib/api";

const FALLBACK = "https://images.unsplash.com/photo-1576933765009-9f57f43910aa?auto=format&fit=crop&w=1200&q=80";
const imgSrc = (p) => (p ? (p.startsWith("http") ? p : fileUrl(p)) : FALLBACK);

/**
 * Responsive photo gallery for the car detail page.
 *   • <md (mobile / small tablet): edge-to-edge horizontal swipeable carousel with snap
 *     points, page-counter pill, prev/next chevrons + a dot indicator strip. Single
 *     image fills the visible width — was the painful mosaic before this refactor.
 *   • ≥md: the original 4-column mosaic (1 hero + 4 tiles) — best for visual storytelling
 *     on bigger screens where horizontal real estate is plentiful.
 *
 * Uses native CSS scroll-snap rather than a 3rd-party carousel lib: ~0kb cost, accessible
 * out of the box, plays nice with screen readers, and feels native to touch users because
 * the OS owns the inertia + bounce animation.
 */
export default function CarGallery({ images = [] }) {
  // Always render at least 1 slide so the hero image never disappears for vendors with
  // an empty image list.
  const slides = images.length > 0 ? images : [null];
  const main = slides[0];
  const others = slides.slice(1, 5);

  // Carousel state for the mobile view.
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);

  // Watch scroll position → derive the active slide index. Throttled with a single rAF
  // so we never burn CPU on a 60fps swipe.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const i = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
        setIndex(i);
        ticking = false;
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(slides.length - 1, i));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
  };

  return (
    <>
      {/* MOBILE: swipeable carousel */}
      <div className="md:hidden -mx-4 sm:-mx-6" data-testid="car-gallery-mobile">
        <div className="relative">
          <div
            ref={trackRef}
            className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar"
            style={{ scrollbarWidth: "none" }}
            data-testid="car-gallery-track"
          >
            {slides.map((p, i) => (
              <div
                key={p || `slot-${i}`}
                className="shrink-0 w-full snap-center bg-neutral-100"
                data-testid={`car-gallery-slide-${i}`}
              >
                <img
                  src={imgSrc(p)}
                  alt=""
                  className="block w-full h-[280px] sm:h-[360px] object-cover"
                  onError={(e) => { e.currentTarget.src = FALLBACK; }}
                />
              </div>
            ))}
          </div>

          {slides.length > 1 && (
            <>
              {/* Page counter pill — instantly tells the user "1 of 5" without staring at dots */}
              <div className="absolute top-3 right-4 px-2.5 py-1 rounded-full bg-black/55 text-white text-xs font-medium tracking-wide" data-testid="car-gallery-counter">
                {index + 1} / {slides.length}
              </div>

              {/* Chevrons — small targets but useful on tablet / non-touch */}
              <button
                type="button"
                onClick={() => goTo(index - 1)}
                aria-label="Previous photo"
                disabled={index === 0}
                className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center disabled:opacity-40 transition"
                data-testid="car-gallery-prev"
              >
                <ChevronLeft className="h-5 w-5 text-neutral-700" />
              </button>
              <button
                type="button"
                onClick={() => goTo(index + 1)}
                aria-label="Next photo"
                disabled={index >= slides.length - 1}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center disabled:opacity-40 transition"
                data-testid="car-gallery-next"
              >
                <ChevronRight className="h-5 w-5 text-neutral-700" />
              </button>

              {/* Dot strip — visual continuity with index, also clickable for fast jumps */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="car-gallery-dots">
                {slides.map((p, i) => (
                  <button
                    key={`dot-${p || i}`}
                    type="button"
                    onClick={() => goTo(i)}
                    aria-label={`Photo ${i + 1}`}
                    className={`h-1.5 rounded-full transition ${i === index ? "bg-white w-5" : "bg-white/60 w-1.5"}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* DESKTOP: mosaic (unchanged grid) */}
      <div className="hidden md:grid grid-cols-4 grid-rows-2 gap-2 h-[480px] rounded-3xl overflow-hidden" data-testid="car-gallery-desktop">
        <div className="col-span-2 row-span-2 bg-neutral-100">
          <img src={imgSrc(main)} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = FALLBACK; }} />
        </div>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-neutral-100">
            <img src={imgSrc(others[i])} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = FALLBACK; }} />
          </div>
        ))}
      </div>
    </>
  );
}
